import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Icon, Menu } from "@fortex/map";
import type { IconName, MenuItem } from "@fortex/map";
import { features } from "../auth/store";
import { loginPath } from "../config";
import type { AppLabels } from "../i18n";
import { useApp, useAuth } from "./context";
import { returnUrlOf } from "./guards";

export type MenuAccess = { signedIn: boolean; canMasterData: boolean; canUsers: boolean; canPublicMaps: boolean };

export type AppMenuEntry = { id: string; label: string; icon: IconName; to: string | null; action?: "sign-out" };

export const adminPaths = { masterData: "/admin/masterdata", maps: "/admin/masterdata/maps", layers: "/admin/masterdata/layers", users: "/admin/users", apiKeys: "/admin/apikeys", publicMaps: "/admin/publicmaps" } as const;

export function menuAccess(can: (feature: string, level: "ReadOnly" | "Full") => boolean, signedIn: boolean): MenuAccess {
  return { signedIn, canMasterData: can(features.masterData, "ReadOnly"), canUsers: can(features.userAdmin, "ReadOnly"), canPublicMaps: can(features.publicMap, "ReadOnly") };
}

export function appMenuEntries(labels: AppLabels, access: MenuAccess): AppMenuEntry[] {
  const entries: AppMenuEntry[] = [{ id: "map", label: labels.menuMap, icon: "map", to: "/" }];

  if (access.canMasterData) {
    entries.push({ id: "masterdata", label: labels.menuMasterData, icon: "database", to: adminPaths.masterData });
    entries.push({ id: "maps", label: labels.menuMaps, icon: "tiles", to: adminPaths.maps });
    entries.push({ id: "layers", label: labels.menuLayers, icon: "layers", to: adminPaths.layers });
  }

  if (access.canUsers) entries.push({ id: "users", label: labels.menuUsers, icon: "users", to: adminPaths.users });
  if (access.canUsers) entries.push({ id: "apikeys", label: labels.menuApiKeys, icon: "login", to: adminPaths.apiKeys });
  if (access.canPublicMaps) entries.push({ id: "publicmaps", label: labels.menuPublicMaps, icon: "link", to: adminPaths.publicMaps });

  entries.push(access.signedIn ? { id: "sign-out", label: labels.signOut, icon: "logout", to: null, action: "sign-out" } : { id: "sign-in", label: labels.signIn, icon: "login", to: "/login" });

  return entries;
}

export function activeEntryId(pathname: string): string {
  if (pathname === adminPaths.maps) return "maps";
  if (pathname === adminPaths.layers) return "layers";
  if (pathname.startsWith(adminPaths.users)) return "users";
  if (pathname.startsWith(adminPaths.apiKeys)) return "apikeys";
  if (pathname.startsWith(adminPaths.publicMaps)) return "publicmaps";
  if (pathname.startsWith(adminPaths.masterData)) return "masterdata";
  if (pathname.startsWith("/login")) return "sign-in";

  return "map";
}

export function AppMenu({ className }: { className?: string }) {
  const { labels, auth } = useApp();
  const signedIn = useAuth(a => a.token !== null);
  const canMasterData = useAuth(a => a.can(features.masterData, "ReadOnly"));
  const canUsers = useAuth(a => a.can(features.userAdmin, "ReadOnly"));
  const canPublicMaps = useAuth(a => a.can(features.publicMap, "ReadOnly"));
  const navigate = useNavigate();
  const location = useLocation();
  const active = activeEntryId(location.pathname);
  const entries = appMenuEntries(labels, { signedIn, canMasterData, canUsers, canPublicMaps });

  const items: MenuItem[] = entries.map(entry => ({
    id: entry.id,
    label: entry.label,
    icon: entry.icon,
    checked: entry.to !== null && entry.action === undefined ? entry.id === active : null,
    separatorBefore: entry.id === "sign-out" || entry.id === "sign-in",
    run: () => {
      if (entry.action === "sign-out") {
        auth.getState().logout();
        navigate(loginPath(returnUrlOf(location.pathname, location.search)));
        return;
      }

      if (entry.id === "sign-in") {
        navigate(loginPath(returnUrlOf(location.pathname, location.search)));
        return;
      }

      if (entry.to !== null) navigate(entry.to);
    }
  }));

  return <Menu label={labels.menu} icon="menu" kind="icon" size="md" placement="bottom-start" items={items} className={className} dataFxmap="app-menu" triggerDataFxmap="app-menu-toggle" />;
}

export function Brand({ subtitle }: { subtitle?: string | null }) {
  const { labels } = useApp();

  return (
    <Link to="/" className="fxapp-brand" data-fxmap="brand">
      <span className="fxapp-brand-name fxmap-display">{labels.appName}</span>
      {subtitle ? <span className="fxapp-brand-sub fxmap-muted">{subtitle}</span> : null}
    </Link>
  );
}

export function TopPill({ menu = true, subtitle = null, children }: { menu?: boolean; subtitle?: string | null; children?: ReactNode }) {
  return (
    <div className={["fxapp-pill", "fxmap-card", menu ? "" : "fxapp-pill-plain"].filter(Boolean).join(" ")} data-fxmap="top-pill">
      {menu ? <AppMenu /> : null}
      <Brand subtitle={subtitle} />
      {children}
    </div>
  );
}

export function AppBar({ title, icon = null, children }: { title: string; icon?: IconName | null; children?: ReactNode }) {
  const { labels } = useApp();
  const user = useAuth(a => a.user);

  return (
    <header className="fxapp-bar" data-fxmap="app-bar">
      <AppMenu />
      <Brand subtitle={labels.adminTitle} />
      <span className="fxapp-bar-divider" aria-hidden="true" />
      <h1 className="fxapp-bar-title fxmap-display fxmap-truncate">
        {icon !== null ? <Icon name={icon} /> : null}
        {title}
      </h1>
      <span className="fxapp-toolbar-grow" />
      {children}
      {user !== null ? <span className="fxapp-bar-user fxmap-muted fxmap-truncate">{user.fullName || user.email || user.name}</span> : null}
      <Link to="/" className="fxapp-bar-link" data-fxmap="back-to-map">
        <Icon name="map" />
        {labels.backToMap}
      </Link>
    </header>
  );
}
